import { Flex, Form } from '@/components';
import { amountDifferenceOperator } from '@/features/project/_models/project-filter';
import { useProjectListSearchQueryStates } from '@/features/search/_hooks/projects-search';
import { useCreateQueryStore } from '@/features/search/_hooks/query-store';
import type { ProjectSearchFormType } from '@/features/search/_models/projects-search';
import {
  type ProjectsSearchSubmitType,
  validateProjectNumber,
} from '@/features/search/_utils/projects-search';
import { searchQueryKeyMap } from '@/features/search/_utils/query-keys';
import { generateQueryAndLeafs } from '@/features/search/_utils/query-store';
import { useInvalidateQuery, useIsFetching } from '@/hooks';
import { isPS } from '@/utils';
import isEqual from 'lodash-es/isEqual';
import { type FC, useCallback } from 'react';
import { useFormContext } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useProjectsSearchFormProviderState } from '../_common/ProjectsSearchAccordionProvider';
import { BasicSearch } from './BasicSearch';
import { ProjectsSearchFieldTags } from './ProjectsSearchFieldTags';
import { SearchButton } from './SearchButton';

const FORM_ID = 'projects-segment-search-accordion';

type Props = {
  onSubmit: (values: ProjectsSearchSubmitType) => Promise<void>;
  onResetPayment: () => void;
  resetCount: number;
};
export const SearchForm: FC<Props> = ({
  onSubmit: _onSubmit,
  onResetPayment,
  resetCount,
}) => {
  const { t } = useTranslation();
  const {
    cascaderTargetIds: currentCascaderValues,
    inputCascaderAndAccountingLabels,
  } = useProjectsSearchFormProviderState();
  const { mutateAsync: createQueryStore } = useCreateQueryStore();
  const isFetching = useIsFetching({ queryKey: searchQueryKeyMap.projects() });
  const { invalidate } = useInvalidateQuery();
  const { searchQueryStates } = useProjectListSearchQueryStates();

  const {
    handleSubmit,
    formState: { isDirty },
    setError,
  } = useFormContext<ProjectSearchFormType>();

  const validateAnalysisAmount = useCallback(
    (values: ProjectSearchFormType) => {
      const { amountSearchConditions } = values;

      if (!amountSearchConditions || amountSearchConditions.length === 0) {
        return { hasError: false };
      }

      let hasError = false;

      amountSearchConditions.forEach((condition, index) => {
        const {
          amountSearchType,
          amountSearchBaseYearWithBudgetType,
          amountSearchDiffOperator,
          amountSearchDiffAmount,
          amountSearchBaseYearWithBudgetTypeEnd,
        } = condition;

        // Check if amountSearchType is required when other fields are filled
        const hasAnyFieldFilled =
          amountSearchBaseYearWithBudgetType ||
          amountSearchDiffOperator ||
          amountSearchDiffAmount !== null ||
          amountSearchBaseYearWithBudgetTypeEnd;

        if (hasAnyFieldFilled && !amountSearchType) {
          setError(`amountSearchConditions.${index}.amountSearchType`, {
            message: t('validation.required'),
          });
          hasError = true;
        }

        if (amountSearchType === 'trend') {
          const isAmountOrEqualOperator =
            amountSearchDiffOperator === amountDifferenceOperator.Values.eq ||
            typeof amountSearchDiffAmount === 'number';

          const trendSearchTargets = [
            amountSearchBaseYearWithBudgetType,
            amountSearchDiffOperator,
            isAmountOrEqualOperator,
            amountSearchBaseYearWithBudgetTypeEnd,
          ];

          if (trendSearchTargets.some((v) => v)) {
            if (trendSearchTargets.some((v) => !v)) {
              setError(`amountSearchConditions.${index}.amountSearchType`, {
                message: t('validation.analysis-all-field-must-required'),
              });
              hasError = true;
            }
          }
        }

        const isAmountOrNullOperator =
          amountSearchDiffOperator === amountDifferenceOperator.Values.none ||
          typeof amountSearchDiffAmount === 'number';

        const thresholdSearchTargets = [
          amountSearchBaseYearWithBudgetType,
          amountSearchDiffOperator,
          isAmountOrNullOperator,
        ];

        if (
          amountSearchType === 'threshold' &&
          thresholdSearchTargets.some((v) => v)
        ) {
          if (thresholdSearchTargets.some((v) => !v)) {
            setError(`amountSearchConditions.${index}.amountSearchType`, {
              message: t('validation.analysis-all-field-must-required'),
            });
            hasError = true;
          }
        }
      });

      return { hasError };
    },
    [t, setError],
  );

  const onSubmit = useCallback(
    async (values: ProjectSearchFormType) => {
      const { hasError: hasProjectNumberError } = validateProjectNumber(
        values,
        setError,
      );
      const { hasError: hasAnalysisAmountError } =
        validateAnalysisAmount(values);
      if (hasProjectNumberError || hasAnalysisAmountError) {
        return;
      }
      const isRS =
        values.sheetType === 'RS' &&
        values.skipAdministrativeReformTeamEvaluation === 'false';

      const newQueryStore = generateQueryAndLeafs({
        otherResponsibleOrganizationIds:
          values.nestedOtherResponsibleOrganizationIds,
        organizationIds: values.nestedOrganizationIds,
        policyIds: isRS ? values.nestedPolicyIds : null,
        expenditureBudgetPurposeIds: isRS
          ? values.nestedExpenditureBudgetPurposeIds
          : null,
      });

      const prevCascaderId = searchQueryStates.cascader;

      const hasChanged = Object.keys(currentCascaderValues).some((key) => {
        const nweQuery = newQueryStore?.query[key];
        const currentCascaderValue = currentCascaderValues[key];
        return !isEqual(nweQuery, currentCascaderValue);
      });

      const isDifferent =
        !isPS() &&
        hasChanged &&
        !isEqual(newQueryStore?.query, currentCascaderValues);

      const newCascadeId =
        isDifferent && newQueryStore
          ? (await createQueryStore(newQueryStore)).id
          : newQueryStore
            ? prevCascaderId
            : undefined;

      await _onSubmit({
        values,
        newCascadeId,
        inputCascaderAndAccountingLabels:
          inputCascaderAndAccountingLabels.current,
      });

      // // Note: If any field's value change then based on query params it will call api anyway, So use isDirty to avoid invalidate call
      if (!isFetching && !isDirty) {
        invalidate(searchQueryKeyMap.projects());
      }
    },
    [
      _onSubmit,
      currentCascaderValues,
      inputCascaderAndAccountingLabels.current,
      invalidate,
      isFetching,
      isDirty,
      createQueryStore,
      validateAnalysisAmount,
      searchQueryStates.cascader,
      setError,
    ],
  );

  return (
    <Form
      leavePageAlertCondition={null}
      id={FORM_ID}
      onFinish={handleSubmit(onSubmit)}
      layout="vertical"
    >
      <Flex vertical gap={16}>
        <BasicSearch resetCount={resetCount} onResetPayment={onResetPayment} />
        <ProjectsSearchFieldTags onResetPayment={onResetPayment} />
        {/* <AdditionalSearch />
        <AnalyticSearch /> */}
        <SearchButton formId={FORM_ID} onResetPayment={onResetPayment} />
      </Flex>
    </Form>
  );
};
